import {
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@ltvco/refresh-lib/theme';
import {
  AttachFile,
  CameraAltOutlined,
  FolderOutlined,
  ImageOutlined,
} from '@mui/icons-material';
import { useRef, ChangeEvent, RefObject, useState } from 'react';

import { DEEPSEARCH_MOBILE_MEDIA_QUERY } from 'components/DeepSearch/constants';

import { INVALID_SIZE_MESSAGE, INVALID_TYPE_MESSAGE } from './constants';
import { getUploadFileValidation } from './fileUtils';

interface FileUploadButtonProps {
  onFileSelect: (file: File) => void;
  onError?: (errorMessage: string) => void;
  disabled?: boolean;
  accept?: string;
}

const IMAGE_ACCEPT = 'image/jpeg,image/png,image/webp,image/gif, image/bmp';
const DEFAULT_ACCEPT = `${IMAGE_ACCEPT},.pdf,application/pdf,.csv,text/csv`;

export const FileUploadButton = ({
  onFileSelect,
  onError,
  disabled = false,
  accept = DEFAULT_ACCEPT,
}: FileUploadButtonProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(DEEPSEARCH_MOBILE_MEDIA_QUERY);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Separate inputs per mobile menu option so each opens the correct native
  // picker (photo library, camera, or general file picker) via `accept` +
  // `capture` attributes. Desktop uses only `fileInputRef` for a single picker.
  const fileInputRef = useRef<HTMLInputElement>(null);
  const photoLibraryInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange =
    (inputRef: RefObject<HTMLInputElement>) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      const { isValidFile, isValidSize } = getUploadFileValidation(file);

      if (file && isValidFile && isValidSize) {
        onFileSelect(file);
      } else if (file && !isValidFile) {
        onError?.(INVALID_TYPE_MESSAGE);
      } else if (file && isValidFile && !isValidSize) {
        onError?.(INVALID_SIZE_MESSAGE);
      }

      if (inputRef.current) {
        inputRef.current.value = '';
      }
    };

  const handleClick = () => {
    if (isMobile) {
      setMenuAnchor(buttonRef.current);
      return;
    }
    fileInputRef.current?.click();
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const openInput = (inputRef: RefObject<HTMLInputElement>) => () => {
    setMenuAnchor(null);
    // Defer the click so the Menu unmounts cleanly before the native picker
    // steals focus — otherwise iOS Safari occasionally cancels the file dialog.
    setTimeout(() => inputRef.current?.click(), 0);
  };

  const iconButtonStyles = {
    color: theme.palette.text.secondary,
    padding: '8px',
    '&:hover': {
      color: theme.palette.primary.main,
      backgroundColor: 'transparent',
    },
    '&:disabled': {
      color: theme.palette.text.disabled,
    },
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        style={{ display: 'none' }}
        onChange={handleFileChange(fileInputRef)}
      />
      {isMobile && (
        <>
          <input
            ref={photoLibraryInputRef}
            type="file"
            accept={IMAGE_ACCEPT}
            style={{ display: 'none' }}
            onChange={handleFileChange(photoLibraryInputRef)}
          />
          <input
            ref={cameraInputRef}
            type="file"
            accept={IMAGE_ACCEPT}
            capture="environment"
            style={{ display: 'none' }}
            onChange={handleFileChange(cameraInputRef)}
          />
        </>
      )}
      <IconButton
        ref={buttonRef}
        onClick={handleClick}
        disabled={disabled}
        sx={iconButtonStyles}
        aria-label="Upload file"
      >
        <AttachFile sx={{ fontSize: '20px' }} />
      </IconButton>
      {isMobile && (
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{
            elevation: 3,
            sx: {
              borderRadius: 3,
              mt: -1.25,
              minWidth: 227,
              '& .MuiMenuItem-root': {
                py: 1.25,
                px: 2,
              },
              '& .MuiListItemIcon-root': {
                minWidth: 36,
                color: 'text.primary',
              },
            },
          }}
        >
          <MenuItem onClick={openInput(photoLibraryInputRef)}>
            <ListItemIcon>
              <ImageOutlined fontSize="small" />
            </ListItemIcon>
            <ListItemText>Photo Library</ListItemText>
          </MenuItem>
          <MenuItem onClick={openInput(cameraInputRef)}>
            <ListItemIcon>
              <CameraAltOutlined fontSize="small" />
            </ListItemIcon>
            <ListItemText>Take Photo</ListItemText>
          </MenuItem>
          <MenuItem onClick={openInput(fileInputRef)}>
            <ListItemIcon>
              <FolderOutlined fontSize="small" />
            </ListItemIcon>
            <ListItemText>Choose Files</ListItemText>
          </MenuItem>
        </Menu>
      )}
    </>
  );
};
